import * as signalR from '@microsoft/signalr';
import urljoin from 'url-join';
import { HUBS_BASEPATH, USE_PIPELINE_STATUS_API_MOCKS } from '@/config/env.config';
import { portalApi } from '@/redux/services/portal/index.api';

const PUBLISHING_STATUS_METHOD = 'PublishingStatus';

/** @type {signalR.HubConnection | null} */
let hubConnection = null;
/** @type {Promise<void>} */
let hubStartPromise = Promise.resolve();

/** Project IDs the UI has joined for pipeline tracker (re-subscribe after reconnect). */
const pipelineTrackerGroupProjectIds = new Set();

/**
 * Creates and starts a SignalR connection to ProjectsHub.
 * Subscribes to legacy hub method `PublishingStatus` (PipelineStatus payload: projectId|pipeline|stage|status) and invalidates PipelineStatus RTK tags (refetch only; payload not stored as UI state).
 * Uses automatic reconnect so the UI recovers when the tab sleeps or network blips.
 * @param {Function} dispatch - Redux dispatch
 * @param {Function} getAccessTokenSilently - Auth0 getAccessTokenSilently
 * @returns {signalR.HubConnection} The connection (call .stop() to disconnect)
 */
export function startProjectsHubConnection(dispatch, getAccessTokenSilently) {
	const hubUrl = urljoin(HUBS_BASEPATH || '', 'projects');
	const connection = new signalR.HubConnectionBuilder()
		.withUrl(hubUrl, {
			accessTokenFactory: async () =>
				await getAccessTokenSilently({ audience: 'https://portal-api' }),
		})
		.withAutomaticReconnect()
		.configureLogging(signalR.LogLevel.Warning)
		.build();

	connection.on(PUBLISHING_STATUS_METHOD, (payload) => {
		// PipelineStatus mock mode is on by default: facts are local-only; skip RTK invalidation churn.
		if (USE_PIPELINE_STATUS_API_MOCKS) return;
		if (typeof payload !== 'string') return;
		const projectId = payload.split('|')[0];
		if (!projectId) return;
		dispatch(
			portalApi.util.invalidateTags([
				{ type: 'pipelineStatusFacts', id: projectId },
				{ type: 'pipelineStatus', id: projectId },
			])
		);
	});

	connection.onreconnected(async () => {
		for (const id of pipelineTrackerGroupProjectIds) {
			try {
				await connection.invoke('JoinPipelineTrackerGroup', id);
			} catch (e) {
				console.warn('ProjectsHub: rejoin pipeline tracker group failed', e);
			}
		}
	});

	hubConnection = connection;
	hubStartPromise = connection.start().catch((err) => {
		console.warn('ProjectsHub SignalR connection failed:', err);
		throw err;
	});

	return connection;
}

/**
 * Join the SignalR group for pipeline tracker updates for one project (server scopes PublishingStatus to this group).
 * @param {string} projectId - Project GUID string
 */
export async function joinProjectPipelineTrackerGroup(projectId) {
	if (!projectId) return;
	try {
		await hubStartPromise;
		if (!hubConnection || hubConnection.state !== signalR.HubConnectionState.Connected) return;
		await hubConnection.invoke('JoinPipelineTrackerGroup', projectId);
		pipelineTrackerGroupProjectIds.add(projectId);
	} catch (e) {
		console.warn('JoinPipelineTrackerGroup failed:', e);
	}
}

/**
 * Leave the pipeline tracker group for a project.
 * @param {string} projectId - Project GUID string
 */
export async function leaveProjectPipelineTrackerGroup(projectId) {
	if (!projectId) return;
	pipelineTrackerGroupProjectIds.delete(projectId);
	try {
		if (hubConnection?.state === signalR.HubConnectionState.Connected) {
			await hubConnection.invoke('LeavePipelineTrackerGroup', projectId);
		}
	} catch (e) {
		console.warn('LeavePipelineTrackerGroup failed:', e);
	}
}
